import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fileUrl, describeError, API } from "@/lib/api";
import { Card } from "@/components/shared";
import { Lightbox } from "@/components/Lightbox";
import { MediaUploader } from "@/components/MediaUploader";
import { FolderBar } from "@/components/gallery/FolderBar";
import { PhotoCard } from "@/components/gallery/PhotoCard";
import { BulkToolbar } from "@/components/gallery/BulkToolbar";
import { ShareDialog, ReportDialog } from "@/components/gallery/PhotoDialogs";
import { UploadCloud } from "lucide-react";

const authedUrl = (path, params = {}) => {
  const q = new URLSearchParams({ ...params, auth: localStorage.getItem("ss_token") || "" });
  return `${API}${path}?${q.toString()}`;
};
// Browser-native download: streams straight to disk, no in-memory blob, no axios timeout
const openDownload = (url) => {
  const a = document.createElement("a"); a.href = url; a.rel = "noopener"; a.target = "_blank"; a.click();
};
const showErr = async (err) => toast.error(await describeError(err));

export function PhotoGallery({ jobId, photos, reload, canUpload, isAdmin, isClient, uploadFile }) {
  const [folders, setFolders] = useState(["PPW", "Before", "After"]);
  const [folder, setFolder] = useState("All");
  const [sel, setSel] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [lb, setLb] = useState(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newFolder, setNewFolder] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [share, setShare] = useState({ note: "", send_email: true, result: null });
  const [reportOpen, setReportOpen] = useState(false);
  const [reportNote, setReportNote] = useState("");

  const loadFolders = useCallback(() => api.get(`/jobs/${jobId}/photo-folders`).then((r) => setFolders(r.data)).catch((e) => console.error("photo-folders load failed", e)), [jobId]);
  useEffect(() => { loadFolders(); }, [loadFolders]);

  const visible = folder === "All" ? photos : photos.filter((p) => (p.folder || "PPW") === folder);
  const counts = photos.reduce((a, p) => { const f = p.folder || "PPW"; a[f] = (a[f] || 0) + 1; return a; }, {});
  const selected = [...sel];
  const targetFolder = folder === "All" ? "PPW" : folder;

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clear = () => { setSel(new Set()); setSelectMode(false); };

  const attachIds = useCallback(async (fileIds, into) => {
    const results = await Promise.allSettled(fileIds.map((fid) => api.post(`/jobs/${jobId}/photos`, { file_id: fid, caption: "Photo", folder: into })));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    results.filter((r) => r.status === "rejected").forEach((r) => console.error("photo attach failed", r.reason));
    reload(); loadFolders();
    if (ok) toast.success(`${ok} photo${ok > 1 ? "s" : ""} added to ${into}`);
    if (ok < fileIds.length) toast.error(`${fileIds.length - ok} failed to attach`);
  }, [jobId, reload, loadFolders]);

  const addFiles = useCallback(async (files, into) => {
    const imgs = Array.from(files).filter((f) => f.type.startsWith("image/") || f.type === "application/pdf");
    if (!imgs.length) return;
    setBusy(true);
    const ups = await Promise.allSettled(imgs.map((f) => uploadFile(f)));
    ups.filter((r) => r.status === "rejected").forEach((r) => console.error("upload failed", r.reason));
    await attachIds(ups.filter((r) => r.status === "fulfilled").map((r) => r.value.id), into);
    setBusy(false);
  }, [uploadFile, attachIds]);

  useEffect(() => {
    if (!canUpload) return undefined;
    const onPaste = (e) => { const files = Array.from(e.clipboardData?.files || []); if (files.length) { e.preventDefault(); addFiles(files, targetFolder); } };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [canUpload, addFiles, targetFolder]);

  const run = async (fn) => { setBusy(true); try { await fn(); } catch (err) { showErr(err); } finally { setBusy(false); } };

  const zip = (ids) => {
    const n = ids.length || photos.length;
    if (n > 150) { toast.error("Up to 150 photos per ZIP — pick a folder or a smaller selection."); return; }
    openDownload(authedUrl(`/jobs/${jobId}/photos/zip`, ids.length ? { ids: ids.join(",") } : {}));
    toast.success(`Preparing ${n} photos — your download will start in a moment`);
  };
  const report = (ids, save) => run(async () => {
    if (ids.length > 60) { toast.error("Up to 60 photos per report — select the key before/after shots."); return; }
    if (save) {
      const { data } = await api.post(`/jobs/${jobId}/photos/report`, { photo_ids: ids, note: reportNote, folder: "save" }, { timeout: 180000 });
      toast.success(`Photo report saved to Documents (${data.filename})`);
    } else {
      openDownload(authedUrl(`/jobs/${jobId}/photos/report`, { ids: ids.join(","), note: reportNote }));
      toast.success("Building your photo report — the PDF will download in a moment");
    }
    setReportOpen(false);
  });
  const move = (to) => run(async () => { await api.post(`/jobs/${jobId}/photos/move`, { photo_ids: selected, folder: to }); toast.success(`Moved ${selected.length} to ${to}`); clear(); reload(); loadFolders(); });
  const remove = () => {
    if (!window.confirm(`Remove ${selected.length} photo${selected.length > 1 ? "s" : ""} from this job?`)) return;
    run(async () => { const { data } = await api.post(`/jobs/${jobId}/photos/delete`, { photo_ids: selected }); toast.success(`${data.deleted} removed`); clear(); reload(); });
  };
  const createFolder = () => {
    if (!newFolder.trim()) return;
    run(async () => { const { data } = await api.post(`/jobs/${jobId}/photo-folders`, { name: newFolder }); setNewFolder(""); await loadFolders(); setFolder(data.name); toast.success(`Folder "${data.name}" ready — drop photos here`); });
  };
  const doShare = () => run(async () => {
    const { data } = await api.post(`/jobs/${jobId}/photos/share`, { photo_ids: selected, note: share.note, send_email: share.send_email });
    setShare((s) => ({ ...s, result: data }));
    if (data.warning) toast.warning(data.warning); else toast.success(data.emailed ? "Link emailed to the client" : "Share link created");
  });
  const copy = async (t) => { try { await navigator.clipboard.writeText(t); toast.success("Link copied"); } catch { toast.error("Copy failed — select the link and copy"); } };

  const toolbarActions = {
    toggleMode: () => (selectMode ? clear() : setSelectMode(true)),
    selectAll: () => setSel(new Set(visible.map((p) => p.id))),
    zipSelected: () => zip(selected),
    zipAll: () => zip(folder === "All" ? [] : visible.map((p) => p.id)),
    openShare: () => { setShare({ note: "", send_email: true, result: null }); setShareOpen(true); },
    openReport: () => { setReportNote(""); setReportOpen(true); },
    openReportAll: () => { setSel(new Set(visible.map((p) => p.id))); setReportNote(""); setReportOpen(true); },
    move, remove,
  };

  return (
    <div className="space-y-4" data-testid="photo-gallery">
      <FolderBar folders={folders} folder={folder} counts={counts} total={photos.length} onPick={(f) => { setFolder(f); clear(); }} onDropMove={move}
        canCreate={canUpload} newFolder={newFolder} setNewFolder={setNewFolder} onCreate={createFolder} hasSelection={selectMode && selected.length > 0} />

      {canUpload && (
        <div data-testid="photo-dropzone" onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files, targetFolder); }}
          className={`border-2 border-dashed p-4 transition-colors ${drag ? "border-[#FF5722] bg-[#FF5722]/10" : "border-zinc-800 bg-black/20"}`}>
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <div className="flex items-center gap-2 text-white font-head font-bold uppercase text-xs"><UploadCloud size={15} className="text-[#FF5722]" /> Drag & drop or paste photos → <span className="text-[#FF5722]">{targetFolder}</span></div>
              <p className="text-zinc-500 text-xs mt-1">Drop a whole batch here, or copy photos and press <kbd className="font-mono bg-zinc-900 px-1">Ctrl/⌘+V</kbd> anywhere on this page. Pick a folder tab first to file them there. Downloads and reports use web-optimized copies (max 1600px) so they're fast and small; tap a photo's save icon for the original.</p>
            </div>
            <MediaUploader upload={uploadFile} onUploaded={(ids) => attachIds(ids, targetFolder)} />
          </div>
          {busy && <div className="font-mono text-xs text-[#FF5722] mt-2 animate-pulse">Working…</div>}
        </div>
      )}

      {photos.length > 0 && (
        <BulkToolbar selectMode={selectMode} selectedCount={selected.length} visibleCount={visible.length} folders={folders} folderLabel={folder === "All" ? "all" : folder}
          isAdmin={isAdmin} isClient={isClient} busy={busy} actions={toolbarActions} />
      )}

      {visible.length === 0 ? (
        <Card className="p-10 text-center text-zinc-600 font-mono">{photos.length === 0 ? "No photos yet." : `No photos in ${folder}.`}</Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {visible.map((p, i) => (
            <PhotoCard key={p.id} photo={p} selected={sel.has(p.id)} selectMode={selectMode} showFolder={folder === "All"} onOpen={() => setLb(i)} onToggle={() => toggle(p.id)} />
          ))}
        </div>
      )}

      <Lightbox items={visible.map((p) => ({ src: fileUrl(p.file_id), name: p.caption || "Photo" }))} index={lb} setIndex={setLb} onClose={() => setLb(null)} />
      <ReportDialog open={reportOpen} onClose={() => setReportOpen(false)} count={selected.length} note={reportNote} setNote={setReportNote} busy={busy} onDownload={() => report(selected, false)} onSave={() => report(selected, true)} />
      <ShareDialog open={shareOpen} onClose={() => { setShareOpen(false); if (share.result) clear(); }} count={selected.length} share={share} setShare={setShare} busy={busy} onSubmit={doShare} onCopy={copy} />
    </div>
  );
}
